import { useMemo, useState } from 'react';
import { useJobApplications } from '../../hooks';
import Spinner from '../common/Spinner';
import ApplicationDrawer from './ApplicationDrawer';
import {
    STATUS_LABELS,
    POSITION_LABELS,
    STORE_LABELS,
} from './constants';

export default function JobApplicationsList() {
    const { applications, loading, refresh } = useJobApplications();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [storeFilter, setStoreFilter] = useState('all');
    const [selectedApplication, setSelectedApplication] = useState(null);

    const filtered = useMemo(() => {
        return applications.filter((app) => {
            if (statusFilter !== 'all' && app.status !== statusFilter) return false;
            if (storeFilter !== 'all' && app.preferredStore !== storeFilter) return false;
            if (searchQuery) {
                const haystack = `${app.firstName} ${app.lastName} ${app.email} ${app.phoneNumber}`.toLowerCase();
                if (!haystack.includes(searchQuery.toLowerCase())) return false;
            }
            return true;
        });
    }, [applications, statusFilter, storeFilter, searchQuery]);

    const counts = useMemo(() => ({
        total: applications.length,
        pending: applications.filter((a) => a.status === 'pending').length,
        contacted: applications.filter((a) => a.status === 'contacted').length,
        hired: applications.filter((a) => a.status === 'hired').length,
    }), [applications]);

    function handleStatusUpdated() {
        refresh();
        setSelectedApplication(null);
    }

    if (loading) return <Spinner />;

    return (
        <>
            <header className="job-apps-header">
                <div>
                    <p className="eyebrow">Recursos Humanos</p>
                    <h1>Solicitudes de Empleo</h1>
                    <p className="subtitle">Revisa y da seguimiento a solicitudes de empleo.</p>
                </div>
                <button type="button" className="secondaryButton" onClick={refresh}>
                    Actualizar
                </button>
            </header>

            {/* Summary cards */}
            <div className="job-apps-summary">
                <div className="custom-summary-card">
                    <span>Total</span><strong>{counts.total}</strong>
                </div>
                <div className="custom-summary-card" style={{ background: '#fff8e1' }}>
                    <span>Pendientes</span><strong>{counts.pending}</strong>
                </div>
                <div className="custom-summary-card" style={{ background: '#e3f2fd' }}>
                    <span>Contactados</span><strong>{counts.contacted}</strong>
                </div>
                <div className="custom-summary-card" style={{ background: '#e8f5e9' }}>
                    <span>Contratados</span><strong>{counts.hired}</strong>
                </div>
            </div>

            {/* Filters */}
            <div className="job-apps-filters">
                <input
                    type="text"
                    placeholder="Buscar por nombre, email, telefono..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="job-apps-search"
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">Todos los estados</option>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>
                <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)}>
                    <option value="all">Todas las tiendas</option>
                    {Object.entries(STORE_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            {filtered.length === 0 ? (
                <p className="job-apps-empty">No se encontraron solicitudes.</p>
            ) : (
                <div className="job-apps-grid-wrapper">
                    <div className="job-apps-grid">
                        <div className="grid-row grid-header">
                            <div>Estado</div>
                            <div>Nombre</div>
                            <div>Puesto</div>
                            <div>Tienda</div>
                            <div>Telefono</div>
                            <div>Fecha</div>
                        </div>
                        {filtered.map((app) => (
                            <div
                                key={app.id}
                                className={`grid-row clickable-order-row ${selectedApplication?.id === app.id ? 'selected' : ''}`}
                                onClick={() => setSelectedApplication(app)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedApplication(app);
                                    }
                                }}
                            >
                                <div>
                                    <span className={`job-app-status ${app.status}`}>
                                        {STATUS_LABELS[app.status] || app.status}
                                    </span>
                                </div>
                                <div>{app.fullName}</div>
                                <div>{POSITION_LABELS[app.positionType] || app.positionType}</div>
                                <div>{STORE_LABELS[app.preferredStore] || app.preferredStore}</div>
                                <div>{app.phoneNumber}</div>
                                <div>{app.createdAt ? new Date(app.createdAt).toLocaleDateString('es-MX') : ''}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Drawer */}
            {selectedApplication && (
                <ApplicationDrawer
                    application={selectedApplication}
                    onClose={() => setSelectedApplication(null)}
                    onStatusUpdated={handleStatusUpdated}
                />
            )}
        </>
    );
}
